import * as dittoData from "DittoData/index";

import {
  getCollectionOfDittoComponents,
  getCorrectPluralForm,
  isWithFormFieldIdInComponentName,
  mapRadioOptions,
  mapSelectOptions,
  replaceHyperlinkVariables,
  replaceNumStringVariables,
} from "Components/TextContext";
import { TextComponentNameLevel1, Variants } from "./textConstants";
import {
  Data,
  Keys,
  Source,
  TextComponentConfig,
  Variant,
} from "./textInterfaces";

import { Option as RadioOption } from "@storeblocks/radio";
import { Option as SelectOption } from "@storeblocks/select";
import { IToggle } from "@unleash/proxy-client-react";
import { HintProps } from "Components/Form/RadioButton/radioButtonInterfaces";
import { ReactNode } from "react";
import { isDefined } from "Utils/functions";

export class TextManager {
  static #SOURCE = dittoData.ditto_component_library;

  private static flags: IToggle[] = [];

  static setFlags(flags: IToggle[]): void {
    TextManager.flags = flags;
  }

  static isFlagEnabled = (flag: string): boolean => {
    return TextManager.flags.some((f) => f.name === flag && f.enabled);
  };

  static getComponent(variant: Variant, id: Keys): Data {
    const source = TextManager.#SOURCE[variant] as Record<Keys, Data>;

    if (!isDefined(source)) {
      throw new Error(
        `Attempted to access an invalid variant of [type="${variant}"]`,
      );
    }

    const data = source[id];

    if (!isDefined(data)) {
      throw new Error(
        `Attempted to access invalid [key="${id}"] in [variant="${variant}"]`,
      );
    }

    return data;
  }

  static getText(id: Keys, config: TextComponentConfig = {}): string {
    const { variant, variables = {}, plurals } = config;
    const textVariant = isDefined(variant) ? variant : Variants.BASE;
    let component = TextManager.getComponent(textVariant, id);

    if (isDefined(component.plurals) && isDefined(plurals)) {
      component = {
        ...component,
        text: getCorrectPluralForm(component.plurals, plurals),
      };
    }

    if (component.hasOwnProperty("variables")) {
      return replaceNumStringVariables(component, variables);
    }

    return component.text;
  }

  static createLinkComponent(
    id: Keys,
    config: TextComponentConfig = {},
    isFormHint = false,
  ): ReactNode | string {
    const { variant, variables } = config;
    const textVariant = isDefined(variant) ? variant : Variants.BASE;
    const component = TextManager.getComponent(textVariant, id);

    if (component.hasOwnProperty("variables")) {
      return isDefined(variables)
        ? replaceHyperlinkVariables(component, isFormHint, variables)
        : replaceHyperlinkVariables(component, isFormHint);
    }

    return "";
  }

  static getTexts(
    ids: Array<Keys>,
    config: TextComponentConfig = {},
    separator: string | Array<string> = " ",
  ): string {
    const { variant, variables } = config;
    const textVariant = isDefined(variant) ? variant : Variants.BASE;
    const arrayOfTexts = ids.map((id) =>
      TextManager.getText(id, { variables, variant: textVariant }),
    );

    if (Array.isArray(separator)) {
      if (ids.length - 1 !== separator.length) {
        throw new Error(
          "Attempted to use dynamic separators with different number of elements",
        );
      }

      return arrayOfTexts.reduce((acc, text, index) => {
        if (index === ids.length - 1) {
          return acc + text;
        }
        return acc + text + separator[index];
      }, "");
    }

    return arrayOfTexts.join(separator);
  }

  static getVariant(
    variant: Variant = Variants.BASE,
  ): Source[Variants.BASE | Variants.ENGLISH | Variants.NEW_TERMS_2025] {
    return TextManager.#SOURCE[variant];
  }

  static getSelectOptions = (
    textComponentNameLevel1: TextComponentNameLevel1,
    formFieldId: string,
    placeholder: string,
    config?: TextComponentConfig,
  ): SelectOption[] => {
    const { optionlabel: optionLabelComponents } =
      getCollectionOfDittoComponents(textComponentNameLevel1);

    const filteredComponents = optionLabelComponents.filter((component) =>
      isWithFormFieldIdInComponentName(component, formFieldId),
    );

    const options = mapSelectOptions(filteredComponents, config);

    return [{ label: placeholder, value: "" }, ...options];
  };

  static getRadioOptions = (
    textComponentNameLevel1: TextComponentNameLevel1,
    formFieldId: string,
    config?: TextComponentConfig,
    hintProps?: HintProps,
  ): RadioOption[] => {
    const { optionlabel: optionLabelComponents } =
      getCollectionOfDittoComponents(textComponentNameLevel1);

    const filteredComponents = optionLabelComponents.filter((component) =>
      isWithFormFieldIdInComponentName(component, formFieldId),
    );

    return mapRadioOptions(filteredComponents, config, hintProps);
  };
}
